import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { srpTrim } from '@salesrun/trim';
import { getValidationState } from '../functions/validateControls';
import { SrpValidationPatterns } from './SrpValidationPatterns';


export class SrpValidators {

    /**
     * Always valid validator (for conditional validators)
     *
     * @static
     * @type {ValidatorFn}
     * @memberof SrpValidators
     */
    static noop: ValidatorFn = () => null;

    static email: ValidatorFn = control => {
        if (!control.value) return null;
        if (!SrpValidationPatterns.checkEmailValidity(<string>control.value)) {
            return { email: true };
        }
        return null;
    };

    static includes = (array: Array<unknown>): ValidatorFn => {
        return control => {
            const value = <unknown>control.value;
            if (value && !array.includes(value)) {
                return { includes: true };
            }
            return null;
        };
    };

    static number: ValidatorFn = control => {
        if (Validators.required(control)) return null;

        const v = <string>control.value;
        return /^(?:-?\d+|-?\d{1,3}(?:,\d{3})+)?(?:\.\d+)?$/.test(v) ? null : { number: true };
    };

    static alpha: ValidatorFn = control => {
        if (!control.value) return null;
        const alphaPattern = /^[a-zA-Z _.,;:!?-]*$/;
        const valid = alphaPattern.test(<string>control.value);
        if (!valid) {
            return { alpha: true };
        }
        return null;
    };

    static unique = (controlName: string): ValidatorFn => {
        return array => {
            if (array instanceof UntypedFormArray) {
                const controlValues = (array.value as unknown[]).map(v => {
                    if (!Object.prototype.hasOwnProperty.call(v, controlName)) {
                        throw new Error(`Control with name "${controlName}" do not exist in FormArray element`);
                    }
                    return v[controlName];
                });
                const duplicates = controlValues.filter((value, index, array) => value && array.indexOf(value) != index);
                array.controls.forEach(ctrl => {
                    const control = ctrl.get(controlName);
                    const invalid = duplicates.includes(control.value);
                    if (invalid && !control.hasError('unique')) {
                        SrpValidators.addErrors({ unique: true }, control);
                        control.markAsTouched();
                    } else if (!invalid && control.hasError('unique')) {
                        SrpValidators.removeErrors(control, ['unique']);
                        control.markAsTouched();
                    }
                });
                if (duplicates.length > 0) {
                    return { unique: true };
                }
            }
            return null;
        };
    };

    /**
     * Validator for unique string in array of strings
     *
     * @static
     * @param {string[] | () => string[]} dataSource
     * @memberof SrpValidators
     */
    static uniqueString = (dataSource: string[] | (() => string[])): ValidatorFn => control => {
        const value = srpTrim(control.value).toLowerCase();
        const data = typeof dataSource === 'function' ? dataSource() : dataSource;
        if (data?.some(s => s.length == value.length && s.toLowerCase().includes(value))) {
            return {
                uniqueString: true,
            };
        }
        return null;
    };

    static fileName: ValidatorFn = control => {
        if (!control.value) return null;
        if (!SrpValidationPatterns.checkFileNameValidity(<string>control.value)) {
            return { fileName: true };
        }
        return null;
    };

    static specialSymbols: ValidatorFn = control => {
        if (!control.value) return null;
        if (!SrpValidationPatterns.excludeSpecialSymbolsValidity(<string>control.value)) {
            return { specialSymbols: true };
        }
        return null;
    };

    static domain: ValidatorFn = control => {
        if (!control.value) return null;

        const value = <string>control.value;
        const pattern = new RegExp('^([\\da-z.-]+)\\.([a-z.]{2,6})$');
        return pattern.test(value) ? null : { domain: true };
    };

    static url: ValidatorFn = control => {
        if (!control.value) return null;

        const value = <string>control.value;
        const pattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
        return pattern.test(value) ? null : { url: true };
    };

    static required: ValidatorFn = control => {
        if (control.value == null) {
            return { required: true };
        }
        if (Array.isArray(control.value)) {
            if (control.value.length === 0) {
                return { required: true };
            }
        }
        if (typeof control.value !== 'string') {
            return null;
        }

        const onlyWhiteSpace = SrpValidators.noTrailingWhiteSpace(control) != null;

        if (control.value.length == 0 || onlyWhiteSpace) {
            return { required: true };
        }

        return null;
    };

    static requiredToSelect = (itemsCount: number): ValidatorFn => {
        return array => {
            if (array.value == null) {
                return { itemsToSelect: true };
            }
            if (array instanceof UntypedFormArray && Array.isArray(array.value)) {
                if (array.value.filter(x => !!x).length < itemsCount) {
                    return { itemsToSelect: true };
                }
            }
            return null;
        };
    };

    static minLengthNoSpaces = (minLength: number): ValidatorFn => {
        return control => {
            if (control.value == null) {
                return null;
            }
            if (typeof (control.value) !== 'string') {
                throw Error(`${SrpValidators.minLengthNoSpaces.name} validator could be applied only to "string" form controls`);
            }
            return control.value.trim().length >= minLength
                ? null
                : { minLengthNoSpaces: true };
        };
    };

    static noTrailingWhiteSpace: ValidatorFn = control => {

        const value = <string>control.value;
        if (value == null || value.length == 0) {
            return null;
        }

        const rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g;
        const trimmed = value.replace(rtrim, '');

        if (trimmed.length == 0) {
            return { whitespace: true };
        }

        return null;
    };

    static noWhiteSpace: ValidatorFn = control => {
        if (!control.value) return null;
        if (!SrpValidationPatterns.anyWhitespace.test(<string>control.value)) {
            return { nowhitespace: true };
        }
        return null;
    };

    static password: ValidatorFn = control => {
        if (!control.value) return null;
        if (!SrpValidationPatterns.passwordValidator.test(<string>control.value)) {
            return { password: true };
        }
        return null;
    };

    /**
     * Validator for forbidden characters rules
     *
     *
     * @static
     * @param {string[]} forbiddenCharacters - not allowed characters
     * @param {string[]} canStarWith - allowed characters at 0 string position
     * @param {string[]} canEndWith - allowed characters at str.length string position
     * @param {string} controlName
     * @param {ValidatorFn} validator
     * @returns {ValidatorFn}
     * @memberof SrpValidators
     */
    static notAllowCharactersValidator = (forbiddenCharacters: string[], cantStartWith?: string[], cantEndWith?: string[]): ValidatorFn => {
        return control => {
            const value: string = control.value;
            if (control.value == null) {
                return null;
            }
            if (typeof (control.value) !== 'string') {
                throw Error(`${SrpValidators.minLengthNoSpaces.name} validator could be applied only to "string" form controls`);
            }
            if (!value) {
                return null;
            }

            if (forbiddenCharacters.some(str => value.includes(str))) {
                return { notAllowCharacters: true };
            }
            if (
                cantStartWith && cantStartWith.length
                && cantStartWith.some(str => str === value[0])
            ) {
                return { notAllowCharacters: true };
            }
            if (
                cantEndWith && cantEndWith.length
                && cantEndWith.some(str => str === value[value.length - 1])
            ) {
                return { notAllowCharacters: true };
            }
            return null;
        };
    };

    /**
     * Compare two fields of FormGroup. Validation error on FormGroup => 'nomatch'
     *
     * @static
     * @param {string} field1Name
     * @param {string} field2Name
     * @returns {ValidatorFn}
     * @memberof CustomValidators
     */

    static fieldMatch(field1Name: string, field2Name: string): ValidatorFn {
        return (group: UntypedFormGroup) => {
            const field1 = group.controls[field1Name];
            const field2 = group.controls[field2Name];

            if (!field1.valid) {
                return null;
            }

            if (field1.value !== field2.value) {
                return { nomatch: true };
            }

            return null;
        };
    }

    // Helper methods


    static validate<T>(value: T, ...validatorFn: ValidatorFn[]): boolean {
        const ctrl = new UntypedFormControl(value, validatorFn);
        return getValidationState(ctrl);
    }

    static validateArray<T>(value: T[], ...validatorFn: ValidatorFn[]): boolean {
        const ctrls = value.map(v => new UntypedFormControl(v));
        const array = new UntypedFormArray(ctrls, validatorFn);
        return getValidationState(array);
    }


    /**
     * Applies on a FormGroup itself
     * Apply any validator to child element even if it disabled.
     * Will return original error prefixed by control name, e.g. userNameRequired for usual required validator.
     *
     * @static
     * @param {string} controlName
     * @param {ValidatorFn} validator
     * @returns {ValidatorFn}
     * @memberof SrpValidators
     */
    static validateControl<T>(controlName: keyof T & string, validator: ValidatorFn | ValidatorFn[]): ValidatorFn {
        return (groupOrArray: AbstractControl): ValidationErrors | null => {
            if (groupOrArray instanceof UntypedFormGroup || groupOrArray instanceof UntypedFormArray) {
                const control = (groupOrArray as FormGroupTyped<T>).controls[controlName];
                const validators = Array.isArray(validator) ? validator : [validator];
                const validationResult = <ValidationErrors>Object.assign({}, ...validators.map(v => v(control as AbstractControl)));
                const errors = Object.keys(validationResult);
                if (errors.length === 0) {
                    return null;
                }
                return errors.reduce((result, nextError) => {
                    const capitalizedError = nextError.charAt(0).toUpperCase() + nextError.substring(1);
                    return {
                        ...result,
                        [`${controlName}${capitalizedError}`]: <unknown>validationResult[nextError],
                    };
                }, {});
            }
            throw new Error('"SrpValidators.validateControl" must be applied to either FormGroup or FormArray');
        };
    }


    /**
     * Applies on a FormGroup
     * Collect errors from Form itself and it controls
     *
     * @static
     * @memberof SrpValidators
     */
    static collectErrors<T>(group: UntypedFormGroup): { valid: boolean, form: ValidationErrors, controls: { [name: string]: ValidationErrors } } {
        if (!(group instanceof UntypedFormGroup)) {
            throw new Error('"SrpValidators.collectErrors" must be applied to either FormGroup');
        }

        const controls = Object
            .keys((group as FormGroupTyped<T>).controls)
            .reduce((result, controlName) => Object.assign(result, { [controlName]: group.controls[controlName].errors }), {});

        return {
            valid: group.valid,
            form: group.errors,
            controls: controls,
        };
    }


    private static removeErrors(control: AbstractControl, keys: string[]): void {
        if (!control || !keys || keys.length === 0) {
            return;
        }

        const remainingErrors = keys.reduce((errors, key) => {
            delete errors[key];
            return errors;
        }, { ...control.errors });

        control.setErrors(remainingErrors);

        if (Object.keys(control.errors || {}).length === 0) {
            control.setErrors(null);
        }
    }

    private static addErrors(errors: { [key: string]: unknown }, control: AbstractControl): void {
        if (!control || !errors) {
            return;
        }

        control.setErrors({ ...control.errors, ...errors });
    }
}